import React from 'react';
import { Link } from 'react-router-dom';
import { useT } from '../i18n';
import LanguagePopover from './LanguagePopover';
import usePageChrome from '../hooks/usePageChrome';
import './AuthLayout.css';

export default function AuthLayout({ children }) {
  const { t } = useT();

  // The same ink `.al-root` fixes for itself (AuthLayout.css). This screen
  // ignores the site theme, so the canvas iOS paints into the status-bar strip
  // and behind Safari's toolbar has to ignore it too — otherwise a light-theme
  // visitor gets near-white bands top and bottom around a near-black page.
  usePageChrome('#0B0D12');

  return (
    <div className="al-root">

      {/* ── LEFT: BRAND PANEL ── */}
      {/* THE PHOTOGRAPH IS SET HERE, NOT IN THE STYLESHEET, and that is a
          build constraint rather than a preference: `url(/images/hero/...)` in
          a CSS file is resolved by webpack as a module import and fails the
          build, because the file lives in `public/` and never passes through
          the bundler. An inline style is a plain runtime string, so nothing
          tries to resolve it.

          The same photography as the landing hero, so signing up does not look
          like a different product — photo2 rather than photo1, so the two
          screens read as a set instead of a repeat. */}
      <aside
        className="al-brand"
        style={{ backgroundImage: 'url(/images/hero/photo2.webp)' }}
      >
        <div className="al-brand-inner">

          <Link to="/" className="al-logo">
            <img src="/images/Tunify Original logo tab.png" alt="Tunify" />
            <span>Tunify</span>
          </Link>

          <div className="al-brand-copy">
            <h1 className="al-headline">
              {t('al_headline_pre')}<br />
              <em>{t('al_headline_hl')}</em>
            </h1>
            <p className="al-tagline">{t('al_tagline')}</p>
          </div>

          <div className="al-stats">
            <div className="al-stat">
              <strong>100%</strong>
              <span>{t('al_stat_free')}</span>
            </div>
            <div className="al-stat-sep" />
            <div className="al-stat">
              <strong>3D</strong>
              <span>{t('al_stat_3d')}</span>
            </div>
            <div className="al-stat-sep" />
            <div className="al-stat">
              <strong>50+</strong>
              <span>{t('al_stat_furn')}</span>
            </div>
          </div>

          <svg className="al-deco" viewBox="0 0 340 240" aria-hidden="true">
            <rect x="12" y="12"  width="155" height="110" fill="none" stroke="rgba(255,255,255,0.13)" strokeWidth="1.5" rx="2"/>
            <rect x="177" y="12" width="152" height="70"  fill="none" stroke="rgba(255,255,255,0.13)" strokeWidth="1.5" rx="2"/>
            <rect x="177" y="92" width="152" height="90"  fill="none" stroke="rgba(255,255,255,0.09)" strokeWidth="1.5" rx="2"/>
            <rect x="12" y="132" width="155" height="96"  fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1.5" rx="2"/>
            <line x1="32"  y1="11" x2="88"  y2="11" stroke="rgba(255,255,255,0.28)" strokeWidth="4" strokeLinecap="round"/>
            <line x1="195" y1="11" x2="258" y2="11" stroke="rgba(255,255,255,0.22)" strokeWidth="4" strokeLinecap="round"/>
            <line x1="11"  y1="160" x2="11" y2="205" stroke="rgba(255,255,255,0.18)" strokeWidth="4" strokeLinecap="round"/>
            <rect x="26" y="146" width="58" height="36" rx="4" fill="none" stroke="rgba(255,255,255,0.09)" strokeWidth="1.2"/>
            <rect x="26" y="146" width="58" height="11" rx="2" fill="rgba(255,255,255,0.05)"/>
            <rect x="190" y="104" width="128" height="12" rx="1" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.09)" strokeWidth="1"/>
            <rect x="302" y="104" width="10"  height="72" rx="1" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.09)" strokeWidth="1"/>
            <circle cx="94" cy="50" r="22" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="1.2"/>
            <rect x="198" y="24" width="110" height="46" rx="3" fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="1.2"/>
            <rect x="204" y="29" width="48"  height="12" rx="2" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.07)" strokeWidth="1"/>
          </svg>

          <div className="al-glow" />
        </div>
      </aside>

      {/* ── RIGHT: FORM PANEL ── */}
      <main className="al-form-panel">
        {/* Top bar: mobile logo + lang switcher */}
        <div className="al-form-topbar">
          <Link to="/" className="al-mobile-logo">
            <img src="/images/Tunify Original logo tab.png" alt="Tunify" />
            <span>Tunify</span>
          </Link>

          {/* Language switcher — always visible */}
          {/* THE SAME GEAR AND DROPDOWN AS THE LANDING NAV, for the same
              reason: three permanent EN/RU/AM buttons on a screen whose whole
              job is one form. Sign-in and sign-up are where someone who
              mis-tapped the language on the way in will try to change it, so
              the control has to be here too — just not spread across the bar.

              No first-visit prompt here: that belongs to the landing page,
              which is where a new visitor actually arrives. */}
          <LanguagePopover triggerClassName="al-gear" />

          {/* NO THEME TOGGLE. These screens set their own palette and ignore
              the site theme (see the note at the top of AuthLayout.css), so
              the control changed nothing you could see on the page you were
              looking at — a switch that appears broken. It is still on every
              page that is actually themed. */}
        </div>

        <div className="al-form-scroll">
          {children}
        </div>
      </main>

    </div>
  );
}
